import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardActionArea,
  Typography,
  Link,
  Chip
} from '@mui/material';
import { LocalOffer } from '@mui/icons-material';
import { API_BASE_URL } from '../utils/api';

interface CategoryPromotionProps {
  categoryId: number;
}

interface Promotion {
  id: number;
  title: string;
  content: string;
  imageUrl?: string;
  linkUrl?: string;
  type: string;
  displayLocation: string;
  isActive: boolean;
  order: number;
}

const CategoryPromotion: React.FC<CategoryPromotionProps> = ({ categoryId }) => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);

  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/promotions?type=category_promotion&categoryId=${categoryId}&isActive=true`
        );
        if (response.ok) {
          const data = await response.json();
          // Filter and sort by order
          const categoryPromos = Array.isArray(data)
            ? data
                .filter((promo: Promotion) => 
                  promo.displayLocation === 'category' || promo.type === 'category_promotion'
                )
                .sort((a: Promotion, b: Promotion) => a.order - b.order)
            : [];
          setPromotions(categoryPromos);
        }
      } catch (error) {
        console.error('Error fetching category promotions:', error);
      }
    };

    if (categoryId) {
      fetchPromotions();
    }
  }, [categoryId]);

  if (promotions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      {promotions.map((promo) => (
        <Card
          key={promo.id}
          sx={{
            mb: 2,
            border: '1px solid #e0e0e0',
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          <CardActionArea
            component={promo.linkUrl ? Link : 'div'}
            href={promo.linkUrl || undefined}
            target={promo.linkUrl ? '_blank' : undefined}
            rel={promo.linkUrl ? 'noopener noreferrer' : undefined}
            onClick={async () => {
              if (promo.id) {
                try {
                  await fetch(`${API_BASE_URL}/api/promotions/${promo.id}/click`, {
                    method: 'POST'
                  });
                } catch (error) {
                  console.error('Error tracking promotion click:', error);
                }
              }
            }}
          >
            {promo.imageUrl && (
              <CardMedia
                component="img"
                height="150"
                image={promo.imageUrl}
                alt={promo.title}
                sx={{ objectFit: 'cover' }}
              />
            )}
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocalOffer sx={{ color: 'primary.main', fontSize: '1rem' }} />
                <Chip
                  label="Promotion"
                  size="small"
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontSize: '0.7rem',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                {promo.title}
              </Typography>
              {promo.content && (
                <Typography variant="body2" color="text.secondary">
                  {promo.content.length > 150 ? `${promo.content.substring(0, 150)}...` : promo.content}
                </Typography>
              )}
            </Box>
          </CardActionArea>
        </Card>
      ))}
    </Box>
  );
};

export default CategoryPromotion;




